import { type RefCallback, useCallback, useLayoutEffect, useEffect, useRef, useState } from 'react';
import { assemblyPositionStore, clampScrollTop } from './assembly-position-store.js';

/**
 * Route-side wiring for F-024 (UAT-09) navigation-state preservation. Reads the
 * in-session position for `trialId` and exposes it as seed props for the
 * Assembly subtree plus write-through change handlers that capture the place as
 * the reviewer works. The route (`uat.tsx`) feeds the seeds into the
 * already-existing `initialOpen*`/`initialQuery` props, wires the handlers, and
 * attaches `contentRef` to the AppShell scroll region; on the next in-session
 * arrival the remounting subtree restores the place.
 *
 * The snapshot is read on every render (NOT frozen): the `/uat` route stays
 * mounted while the nested Test Case route unmounts the assembly subtree, so
 * each return re-renders the route and must re-seed the *current* snapshot
 * (research R4).
 *
 * Scroll (US2): `contentRef` is a callback ref that tracks the scroll container
 * in state so the effects react to its mount/unmount. A rAF-throttled `scroll`
 * listener captures the offset (write-through). Restore runs in a layout effect
 * gated on `ready` (the Test Script data being present, so the list has its real
 * height — not the "Loading…" placeholder) and fires once per element
 * attachment, setting the clamped `scrollTop` (research R3/R4).
 *
 * @param ready whether the Test Script data has loaded (gates scroll restore).
 */
export type UseAssemblyPositionResult = {
  readonly initialOpenSectionIds: readonly string[];
  readonly initialOpenSubsectionIds: readonly string[];
  readonly initialQuery: string;
  readonly onOpenSectionIdsChange: (ids: readonly string[]) => void;
  readonly onOpenSubsectionIdsChange: (ids: readonly string[]) => void;
  readonly onQueryChange: (query: string) => void;
  readonly contentRef: RefCallback<HTMLDivElement>;
};

export function useAssemblyPosition(trialId: string, ready = false): UseAssemblyPositionResult {
  const snapshot = assemblyPositionStore.readPosition(trialId);

  const onOpenSectionIdsChange = useCallback(
    (ids: readonly string[]) => assemblyPositionStore.captureOpenSections(trialId, ids),
    [trialId],
  );
  const onOpenSubsectionIdsChange = useCallback(
    (ids: readonly string[]) => assemblyPositionStore.captureOpenSubsections(trialId, ids),
    [trialId],
  );
  const onQueryChange = useCallback(
    (query: string) => assemblyPositionStore.captureQuery(trialId, query),
    [trialId],
  );

  // Track the scroll container in state so the effects below re-run when it
  // mounts (on return) and unmounts (on navigating to a case).
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const contentRef = useCallback<RefCallback<HTMLDivElement>>((el) => setContentEl(el), []);

  // Capture: write the scroll offset through to the store, throttled to one
  // capture per animation frame so a fast scroll doesn't thrash the store.
  useEffect(() => {
    if (!contentEl) return;
    let frame: number | null = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        assemblyPositionStore.captureScrollTop(trialId, contentEl.scrollTop);
      });
    };
    contentEl.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      contentEl.removeEventListener('scroll', onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [contentEl, trialId]);

  // Restore: once the element is attached AND the data is ready, set the clamped
  // scroll offset exactly once per attachment (a ref guard, so live scrolling is
  // never clobbered by a later re-render). A missing entry is a no-op (FR-008).
  const restoredEl = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!contentEl || !ready) return;
    if (restoredEl.current === contentEl) return;
    restoredEl.current = contentEl;
    const stored = assemblyPositionStore.readPosition(trialId);
    if (!stored) return;
    contentEl.scrollTop = clampScrollTop(
      stored.scrollTop,
      contentEl.scrollHeight - contentEl.clientHeight,
    );
  }, [contentEl, ready, trialId]);

  return {
    initialOpenSectionIds: snapshot?.openSectionIds ?? [],
    initialOpenSubsectionIds: snapshot?.openSubsectionIds ?? [],
    initialQuery: snapshot?.query ?? '',
    onOpenSectionIdsChange,
    onOpenSubsectionIdsChange,
    onQueryChange,
    contentRef,
  };
}
